import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as analytics from "@/api/analytics";

// ═══════════════════════════════════════════════════════════════════
// Telemetry — write one end, read the other.
//
// The point of this page is that the pipe can be PROVEN by hand: fire an
// event, then read the aggregate that should have moved. Analytics is the
// easiest thing in a system to have confidently broken, because nothing
// complains when it stops — the numbers just quietly become wrong.
//
// Two things worth knowing:
//
//   · The writes take NO auth, on purpose. A page view from somebody not
//     signed in is still a page view, and the most interesting searches come
//     from people who have not joined yet.
//   · The writes are also the ONE place in this API that does not use the
//     `{ success, data }` envelope — the id comes back at the top level.
//   · `zeroResultQueries` is the most valuable list here and the least
//     obvious. Everything else measures what WeDeshi already does; that one
//     measures what it was ASKED for and could not do — demand that leaves no
//     other trace anywhere in the system.
// ═══════════════════════════════════════════════════════════════════

const TABS = ["send", "mine", "platform", "search"] as const;
type Tab = (typeof TABS)[number];

export default function TelemetryLab() {
  const [tab, setTab] = useState<Tab>("send");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Telemetry"
        subtitle="Fire an event, then read the aggregate it should have moved."
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Telemetry" }]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex gap-1 flex-wrap mb-4 border-b border-gray-200 pb-3">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                tab === t ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "send" && <Send />}
        {tab === "mine" && <Mine />}
        {tab === "platform" && <Platform />}
        {tab === "search" && <SearchAnalytics />}
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

/** Admin reads refuse for ordinary members; say so rather than showing blanks. */
function AdminRefusal({ error }: { error: unknown }) {
  return (
    <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
      This is admin-only, so an ordinary member is refused — the guard working,
      not an outage. The server said:{" "}
      <em>{error instanceof Error ? error.message : "forbidden"}</em>
    </p>
  );
}

// ── Send ───────────────────────────────────────────────────────────

function Send() {
  const qc = useQueryClient();
  const [eventName, setEventName] = useState("lab_probe");
  const [eventType, setEventType] = useState<string>("feature_use");
  const [query, setQuery] = useState("bindi");
  const [resultCount, setResultCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const say = (line: string) => setLog((l) => [line, ...l].slice(0, 8));

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
      await qc.invalidateQueries({ queryKey: ["an-platform"] });
      await qc.invalidateQueries({ queryKey: ["an-search"] });
      await qc.invalidateQueries({ queryKey: ["an-mine"] });
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">A generic event</p>
        <p className="text-xs text-gray-500 mb-2">
          Fire-and-forget: the reply carries an id, or <code>null</code> when the
          write was swallowed. A swallowed write is not an error — telemetry
          must never be the reason a page fails.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            {analytics.ANALYTICS_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={!eventName.trim()}
            onClick={() =>
              run(async () => {
                const r = await analytics.trackEvent({
                  eventType,
                  eventName: eventName.trim(),
                  pageUrl: "/lab/telemetry",
                });
                return `event → id ${r.id ?? "null (swallowed)"}`;
              })
            }
          >
            Send
          </Button>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-1">A search</p>
        <p className="text-xs text-gray-500 mb-2">
          Set the result count to <strong>0</strong> to record a search that
          found nothing. That is the case worth recording: it is the only
          evidence anywhere that somebody wanted something WeDeshi could not
          show them.
        </p>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="bindi"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <input
            type="number"
            min={0}
            value={resultCount}
            onChange={(e) => setResultCount(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-24"
          />
          <span className="text-sm text-gray-500">results</span>
          <Button
            size="sm"
            variant="secondary"
            isLoading={busy}
            disabled={!query.trim()}
            onClick={() =>
              run(async () => {
                const r = await analytics.trackSearchQuery({
                  query: query.trim(),
                  resultCount,
                  category: "all",
                });
                return `search "${query.trim()}" (${resultCount} results) → id ${
                  r.id ?? "null"
                }${resultCount === 0 ? " · recorded as a MISS" : ""}`;
              })
            }
          >
            Record search
          </Button>
        </div>
      </div>

      {log.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-500 mb-1">What went out</p>
          <ul className="space-y-1">
            {log.map((l, i) => (
              <li key={i} className="text-xs text-gray-700 font-mono">
                {l}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Mine ───────────────────────────────────────────────────────────

function Mine() {
  const { data, isLoading } = useQuery({
    queryKey: ["an-mine"],
    queryFn: () => analytics.getMyProfileAnalytics(),
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  const d = data?.data;
  if (!d) return <Empty>No profile analytics.</Empty>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {new Date(d.period.start).toLocaleDateString()} —{" "}
        {new Date(d.period.end).toLocaleDateString()}
      </p>
      <Stat label="views of your profile" value={d.metrics.totalViews} />

      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">Who looked</p>
        <p className="text-xs text-gray-500 mb-2">
          A viewer can be <strong>null</strong> — somebody signed out is counted
          but never named. The count is the fact; the identity is not always
          ours to give.
        </p>
        {!d.metrics.topViewers.length ? (
          <Empty>Nobody yet.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {d.metrics.topViewers.map((v, i) => (
              <li key={i} className="text-sm flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {v.user?.name ?? "Someone signed out"}
                </span>
                {v.user?.city && <span className="text-xs text-gray-500">{v.user.city}</span>}
                <Badge variant="gray">{v.viewCount}×</Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Platform ───────────────────────────────────────────────────────

function Platform() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["an-platform"],
    queryFn: () => analytics.getPlatformAnalytics(),
    retry: false,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  if (error) return <AdminRefusal error={error} />;
  const d = data?.data;
  if (!d) return <Empty>No platform analytics.</Empty>;

  return (
    <div className="space-y-5">
      <div className="flex gap-6 flex-wrap">
        <Stat label="events" value={d.overview.totalEvents} />
        <Stat label="active people" value={d.overview.activeUsers} />
      </div>

      {d.eventsByType.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-900 mb-1">By type</p>
          <div className="flex gap-1.5 flex-wrap">
            {d.eventsByType.map((e) => (
              <Badge key={e.type} variant="blue">
                {e.type.replace(/_/g, " ")}: {e.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {d.topSearches.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-900 mb-1">Most searched</p>
          <div className="flex gap-1.5 flex-wrap">
            {d.topSearches.map((s, i) => (
              <Badge key={i} variant="gray">
                {s.query} · {s.count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {d.deviceBreakdown.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-900 mb-1">Devices</p>
          <div className="flex gap-1.5 flex-wrap">
            {/* device is nullable — unknown is a real bucket, not a gap. */}
            {d.deviceBreakdown.map((x, i) => (
              <Badge key={i} variant="gray">
                {x.device ?? "unknown"}: {x.count}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Search analytics ───────────────────────────────────────────────

function SearchAnalytics() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["an-search"],
    queryFn: () => analytics.getSearchAnalytics(),
    retry: false,
  });

  if (isLoading) return <Empty>Loading…</Empty>;
  if (error) return <AdminRefusal error={error} />;
  const d = data?.data;
  if (!d) return <Empty>No search analytics.</Empty>;

  return (
    <div className="space-y-5">
      <div className="flex gap-6 flex-wrap">
        <Stat label="searches" value={d.overview.totalSearches} />
        <Stat label="avg results" value={d.overview.avgResultCount} />
        <Stat
          label="click-through"
          value={`${d.overview.clickThroughRate}%`}
          hint="already a rate"
        />
      </div>

      {d.topQueries.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-900 mb-1">What people look for</p>
          <ul className="space-y-1">
            {d.topQueries.map((q, i) => (
              <li key={i} className="text-sm flex items-center gap-2">
                <span className="font-medium text-gray-900">{q.query}</span>
                <Badge variant="gray">{q.count}×</Badge>
                <span className="text-xs text-gray-500">
                  averaging {q.avgResults} result{q.avgResults === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-1">
          Asked for, and not found
        </p>
        <p className="text-xs text-gray-500 mb-2">
          The most valuable list here. Everything above measures what WeDeshi
          already does; this measures what it was asked for and could not do —
          demand that leaves no other trace in the system. It is the evidence
          base for search and discovery, and it names the businesses worth going
          out to recruit.
        </p>
        {!d.zeroResultQueries.length ? (
          <Empty>
            Nothing came back completely empty. Read the list below before
            concluding everything was found — a search for a trade nobody
            offers comes back FULL of near neighbours, so it never appears
            here.
          </Empty>
        ) : (
          <ul className="space-y-1">
            {d.zeroResultQueries.map((q, i) => (
              <li key={i} className="text-sm flex items-center gap-2">
                <span className="font-medium text-red-800">{q.query}</span>
                <Badge variant="yellow">{q.count}× with nothing to show</Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── the list the one above cannot produce ──────────────────── */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-900 mb-1">
          Words nobody here has ever written
        </p>
        <p className="text-xs text-gray-500 mb-2">
          A search for "wedding photographer" returns fifteen results — a decor
          firm, a bridal salon, a memory board — and not one photographer. The
          count says fifteen, so the list above stays silent. This one does not:
          it counts the individual WORDS that appear in no document at all. Each
          is a trade or a product nobody on the platform offers.
          <br />
          Read the top, not the tail. A real gap is asked for again and again;
          an incidental word — "canal" from "root canal" — turns up once.
        </p>
        {!d.unansweredTerms?.length ? (
          <Empty>
            Every word people searched for exists somewhere in the index.
          </Empty>
        ) : (
          <ul className="space-y-1">
            {d.unansweredTerms.map((t, i) => (
              <li key={i} className="text-sm flex items-center gap-2">
                <span className="font-medium text-amber-900">{t.term}</span>
                <Badge variant="yellow">
                  {t.count}× asked, 0 documents contain it
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
